import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BookOpen, CheckCircle2, Download, AlertTriangle, Factory } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Manuais() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [docs, setDocs] = useState(null);
  const [alertas, setAlertas] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [salvandoCfg, setSalvandoCfg] = useState(false);

  const load = () => {
    api.get("/manuais").then(r => setDocs(r.data)).catch(e => toast.error(formatApiError(e)));
    api.get("/alertas-defeito").then(r => setAlertas(r.data)).catch(() => {});
    api.get("/ranking-defeitos").then(r => setRanking(r.data)).catch(() => {});
    if (isAdmin) api.get("/admin/config/alerta-defeito").then(r => setCfg(r.data)).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const salvarCfg = async () => {
    setSalvandoCfg(true);
    try {
      await api.put("/admin/config/alerta-defeito", cfg);
      toast.success("Índice de alerta atualizado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSalvandoCfg(false);
  };

  const curar = async (chave, coletaId) => {
    if (!window.confirm("Manter esta coleta como a melhor e excluir as demais deste produto?")) return;
    try {
      await api.post(`/manuais/${chave}/curar`, { coleta_id: coletaId });
      toast.success("Curadoria salva — demais coletas removidas");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!docs) return <div className="aj-card p-10 text-center text-aj-text-muted">Carregando manuais…</div>;

  return (
    <div className="space-y-5" data-testid="manuais-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Base de Conhecimento</div>
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-aj-blue-deep mt-1 flex items-center gap-2">
          <BookOpen size={24} className="text-aj-orange" /> Manuais de Produto
        </h1>
        <p className="text-sm text-aj-text-muted mt-1 max-w-2xl">
          Fotos coletadas pelos montadores (até 5 coletas por produto). Escolha a mais nítida e exclua as demais —
          a base servirá para os futuros manuais 3D.
        </p>
      </div>

      {alertas?.alertas?.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-testid="alerta-defeito-banner">
          <div className="flex items-center gap-2 font-bold text-[#B91C1C] text-sm">
            <AlertTriangle size={16} /> Possível defeito de fábrica — índice de assistências acima de {alertas.limiar_pct}%
          </div>
          <div className="mt-2 space-y-1">
            {alertas.alertas.map(a => (
              <div key={a.chave} className="text-xs text-[#7F1D1D]" data-testid={`alerta-defeito-${a.chave}`}>
                <strong>{a.produto}</strong>: {a.assistencias} assistência(s) em {a.montagens} montagem(ns) —{" "}
                <strong>{a.indice_pct}%</strong>. Recomendamos avaliar o lote junto ao fabricante.
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && cfg && (
        <div className="aj-card p-4 flex flex-wrap items-end gap-3" data-testid="alerta-defeito-config">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">Índice de alerta (%)</label>
            <Input type="number" min={1} max={100} value={cfg.indice_pct} className="h-8 w-24 mt-1"
              onChange={e => setCfg({ ...cfg, indice_pct: e.target.value })} data-testid="alerta-defeito-config-pct" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">Mínimo de montagens</label>
            <Input type="number" min={1} max={1000} value={cfg.minimo_montagens} className="h-8 w-24 mt-1"
              onChange={e => setCfg({ ...cfg, minimo_montagens: e.target.value })} data-testid="alerta-defeito-config-min" />
          </div>
          <Button size="sm" className="h-8" onClick={salvarCfg} disabled={salvandoCfg} data-testid="alerta-defeito-config-salvar">
            {salvandoCfg ? "Salvando…" : "Salvar índice"}
          </Button>
          <span className="text-[11px] text-aj-text-muted">
            A loja é avisada no painel quando um produto ultrapassa este índice de assistências.
          </span>
        </div>
      )}

      {ranking?.length > 0 && (
        <div className="aj-card p-5" data-testid="ranking-defeitos">
          <h2 className="font-bold text-aj-blue-deep flex items-center gap-2 text-sm uppercase tracking-wider">
            <Factory size={16} className="text-aj-orange" /> Ranking de Defeitos por Fabricante
          </h2>
          <p className="text-xs text-aj-text-muted mt-1">Use estes dados para negociar com os fornecedores.</p>
          <div className="mt-3 space-y-3">
            {ranking.map(f => (
              <div key={f.fabricante} className="border border-border rounded-lg p-3" data-testid={`ranking-fab-${f.fabricante}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-bold text-sm text-aj-blue-deep">{f.fabricante}</span>
                  <span className="text-xs text-aj-text-muted">
                    {f.montagens} montagem(ns) · {f.assistencias} assistência(s) ({f.indice_pct}%) · {f.defeitos} defeito(s) de peça
                  </span>
                </div>
                <div className="mt-2 space-y-1.5">
                  {f.produtos.filter(p => p.assistencias > 0 || p.defeitos > 0).slice(0, 10).map((p, i) => (
                    <div key={i} className="text-xs flex items-start gap-2">
                      <span className="font-mono font-bold text-aj-orange shrink-0">{i + 1}º</span>
                      <div>
                        <span className="font-semibold">{p.produto}</span>{" "}
                        <span className="text-aj-text-muted">— {p.assistencias}/{p.montagens} assistências ({p.indice_pct}%)</span>
                        {p.pecas?.length > 0 && (
                          <span className="ml-1">
                            {p.pecas.map((pc, j) => (
                              <span key={j} className="inline-block ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-[#B91C1C] border border-red-100">
                                {pc.qtd}x {pc.item}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {f.produtos.every(p => !p.assistencias && !p.defeitos) && (
                    <div className="text-xs text-aj-text-muted">Sem defeitos registrados.</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {docs.length === 0 && <div className="aj-card p-10 text-center text-aj-text-muted">Nenhum manual coletado ainda.</div>}

      {docs.map(d => (
        <div key={d.chave} className="aj-card p-5" data-testid={`manual-${d.chave}`}>
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h2 className="font-bold text-aj-blue-deep flex items-center gap-2">
                {d.produto}
                {d.ficha?.alerta_defeito && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-[#B91C1C]"
                    data-testid={`manual-alerta-defeito-${d.chave}`}>
                    <AlertTriangle size={11} /> Possível defeito de fábrica ({d.ficha.indice_pct}%)
                  </span>
                )}
              </h2>
              <div className="text-xs text-aj-text-muted mt-0.5">
                {d.coletas?.length || 0}/5 coleta(s){d.curado_por ? ` · curado por ${d.curado_por}` : ""}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-right text-xs" data-testid={`manual-ficha-${d.chave}`}>
                <div><strong>Ficha:</strong> {d.ficha?.montagens} montagem(ns) · assistências {d.ficha?.indice}</div>
                {d.ficha?.volumes_registrados && <div>Volumes: {d.ficha.volumes_registrados}</div>}
              </div>
              {d.pdf_url && (
                <a href={d.pdf_url} download>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" data-testid={`manual-pdf-${d.chave}`}>
                    <Download size={13} /> Baixar PDF
                  </Button>
                </a>
              )}
            </div>
          </div>

          {d.ficha?.pecas?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {d.ficha.pecas.map((p, i) => (
                <span key={i} className="px-2 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-[#B91C1C] border border-red-100">
                  {p.qtd}x {p.item}
                </span>
              ))}
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {(d.coletas || []).map(c => (
              <div key={c.id} className={`border rounded-lg p-3 ${c.melhor ? "border-[#16A34A] bg-[#F0FDF4]" : "border-border"}`}
                data-testid={`manual-coleta-${c.id}`}>
                <div className="flex justify-between items-center text-xs mb-2">
                  <span className="font-mono text-aj-text-muted">{c.pim_code} · {c.qtd_fotos} folha(s)</span>
                  {c.melhor ? (
                    <span className="inline-flex items-center gap-1 text-[#16A34A] font-bold"><CheckCircle2 size={13} /> Melhor</span>
                  ) : (
                    <Button size="sm" variant="outline" className="h-6 text-[10px]"
                      onClick={() => curar(d.chave, c.id)} data-testid={`manual-curar-${c.id}`}>
                      Manter esta · excluir demais
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(c.fotos || []).slice(0, 8).map((f, i) => (
                    <a key={i} href={f.url} target="_blank" rel="noreferrer">
                      <img src={f.url} alt={`folha ${i + 1}`} className="h-16 w-16 object-cover rounded border border-border hover:opacity-80" />
                    </a>
                  ))}
                  {(c.fotos || []).length > 8 && <span className="text-[10px] text-aj-text-muted self-end">+{c.fotos.length - 8}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
